import React, { useState, useCallback, useEffect } from 'react';
import { useAdmin } from '../context/AdminContext';
import { ReasoningStepsEditor } from '../components/ReasoningStepsEditor';
import { TagMultiSelector } from '../components/TagMultiSelector';
import type { ReasoningMetadata, ReferencesMetadata } from '@pineseed/spec';
import { v7 as uuidv7 } from 'uuid';

export function ComposerPanel() {
  const { adminApp, composer, thread } = useAdmin();
  
  const [searchParticipantId, setSearchParticipantId] = useState('');
  const [formData, setFormData] = useState({
    participantId: '',
    content: '',
    threadId: '',
    conversationId: '',
    workspaceId: adminApp.workspaceId,
    reasoning: { steps: [], conclusion: '' } as ReasoningMetadata,
    references: [] as ReferencesMetadata,
  });
  const [messageRole, setMessageRole] = useState<'user' | 'assistant' | 'system'>('user');
  const [sending, setSending] = useState(false);

  // Toast notifications for composing events
  const [toasts, setToasts] = useState<Array<{ id: string; participantId: string; content: string }>>([]);

  // Sync workspace ID
  useEffect(() => {
    setFormData((prev) => ({ ...prev, workspaceId: adminApp.workspaceId }));
  }, [adminApp.workspaceId]);

  // Update form data when composition loads or clears
  const compositionData = composer.composition;
  const isLoadingComposition = composer.loading;
  const currentWorkspaceId = adminApp.workspaceId;
  
  useEffect(() => {
    if (compositionData) {
      setFormData({
        participantId: compositionData.participantId,
        content: compositionData.content,
        threadId: compositionData.threadId,
        conversationId: compositionData.conversationId,
        workspaceId: compositionData.workspaceId,
        reasoning: compositionData.reasoning || { steps: [], conclusion: '' },
        references: compositionData.references || [],
      });
    } else if (!isLoadingComposition && compositionData === null && searchParticipantId.trim()) {
      // Clear form when composition is explicitly null (not found), but keep participantId from search
      setFormData({
        participantId: searchParticipantId.trim(),
        content: '',
        threadId: '',
        conversationId: '',
        workspaceId: currentWorkspaceId,
        reasoning: { steps: [], conclusion: '' },
        references: [],
      });
    }
  }, [compositionData, isLoadingComposition, currentWorkspaceId, searchParticipantId]);

  // Subscribe to composing events and show toasts
  // Serialize Map for React reactivity detection
  const composingEventsKey = JSON.stringify(
    Array.from(composer.composingEvents.entries()).map(([id, event]) => [id, event.timestamp])
  );
  
  useEffect(() => {
    console.log('🍞 [ComposerPanel] Composing events changed, total events:', composer.composingEvents.size);
    
    composer.composingEvents.forEach((event, participantId) => {
      // Don't show toasts for the current participant being edited
      if (participantId === formData.participantId) {
        console.log('⏭️ [ComposerPanel] Skipping toast for current participant:', participantId);
        return;
      }

      const toastId = `${participantId}-${event.timestamp}`;
      console.log('🍞 [ComposerPanel] Processing event for toast:', toastId, event.content);
      
      // Add toast if not already present
      setToasts((prev) => {
        if (prev.some((t) => t.id === toastId)) {
          console.log('⏭️ [ComposerPanel] Toast already exists:', toastId);
          return prev;
        }
        console.log('✅ [ComposerPanel] New toast added:', toastId);
        return [...prev, { id: toastId, participantId, content: event.content }];
      });

      // Remove toast after 500ms
      setTimeout(() => {
        console.log('🗑️ [ComposerPanel] Removing toast:', toastId);
        setToasts((prev) => prev.filter((t) => t.id !== toastId));
      }, 500);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composingEventsKey, formData.participantId]);

  // Load composition when participant ID is searched
  const handleSearch = useCallback(async () => {
    if (!searchParticipantId.trim()) return;
    await composer.loadComposition(searchParticipantId.trim());
  }, [searchParticipantId, composer]);

  // Handle field changes and trigger auto-save
  const handleFieldChange = useCallback((field: keyof typeof formData, value: unknown) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      
      // Queue auto-save if data has changed (content, reasoning, references)
      if (field === 'content' || field === 'reasoning' || field === 'references') {
        if (updated.participantId && updated.content && updated.threadId && updated.conversationId && updated.workspaceId) {
          composer.queueAutoSave(updated);
        }
      }
      
      return updated;
    });
  }, [composer]);

  // Reset composition
  const handleReset = useCallback(async () => {
    const participantId = searchParticipantId.trim();
    if (!participantId) return;
    
    if (confirm(`Are you sure you want to reset the composition for participant ${participantId}?`)) {
      await composer.resetComposition(participantId);
      setFormData({
        participantId: '',
        content: '',
        threadId: '',
        conversationId: '',
        workspaceId: adminApp.workspaceId,
        reasoning: { steps: [], conclusion: '' },
        references: [],
      });
      setSearchParticipantId('');
    }
  }, [searchParticipantId, composer, adminApp.workspaceId]);

  // Send composition as message
  const handleSend = useCallback(async () => {
    if (!formData.participantId || !formData.content || !formData.threadId || !formData.conversationId || !formData.workspaceId) {
      return;
    }

    setSending(true);
    try {
      const messageId = uuidv7();
      await thread.addMessagesToThread({
        threadId: formData.threadId,
        conversationId: formData.conversationId,
        workspaceId: formData.workspaceId,
        messages: [{
          id: messageId,
          content: formData.content,
          role: messageRole,
          participantId: formData.participantId,
          threadId: formData.threadId,
          conversationId: formData.conversationId,
          workspaceId: formData.workspaceId,
          timestamp: new Date().toISOString(),
          reasoning: formData.reasoning,
          references: formData.references,
        } as any],
      });

      // Show success toast
      const toastId = `send-${Date.now()}`;
      setToasts((prev) => [...prev, { id: toastId, participantId: formData.participantId, content: 'Message sent successfully!' }]);
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== toastId));
      }, 3000);
    } catch (error) {
      console.error('Failed to send message:', error);
      // Show error toast
      const toastId = `error-${Date.now()}`;
      setToasts((prev) => [...prev, { id: toastId, participantId: formData.participantId, content: 'Failed to send message' }]);
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== toastId));
      }, 3000);
    } finally {
      setSending(false);
    }
  }, [formData, messageRole, thread]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Composer</h2>
        {composer.isSaving && (
          <div className="text-sm text-blue-500 flex items-center gap-2">
            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Auto-saving...
          </div>
        )}
      </div>

      {/* Toast notifications */}
      {toasts.length > 0 && (
        <div className="fixed top-4 right-4 z-50 space-y-2">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className="bg-blue-500 text-white px-4 py-2 rounded shadow-lg flex items-center gap-2 animate-fade-in"
            >
              <span className="text-xs font-medium">{toast.participantId}</span>
              <span className="text-xs opacity-75">is composing: {toast.content}</span>
            </div>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Search Composition</h3>
        <div className="flex gap-2">
          <input
            type="text"
            value={searchParticipantId}
            onChange={(e) => setSearchParticipantId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Enter participant ID"
            className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
          <button
            onClick={handleSearch}
            disabled={composer.loading || !searchParticipantId.trim()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {composer.loading ? 'Loading...' : 'Search'}
          </button>
          <button
            onClick={handleReset}
            disabled={composer.loading || !searchParticipantId.trim()}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset
          </button>
        </div>
        {composer.error && (
          <div className="mt-2 text-sm text-red-500">{composer.error}</div>
        )}
      </div>

      {/* Composition Form */}
      {formData.participantId && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Composition for {formData.participantId}
            </h3>
          </div>

          {/* Participant ID (read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Participant ID
            </label>
            <input
              type="text"
              value={formData.participantId}
              readOnly
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 cursor-not-allowed"
            />
          </div>

          {/* Thread ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Thread ID *
            </label>
            <input
              type="text"
              value={formData.threadId}
              onChange={(e) => handleFieldChange('threadId', e.target.value)}
              placeholder="Thread UUID"
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          {/* Conversation ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Conversation ID *
            </label>
            <input
              type="text"
              value={formData.conversationId}
              onChange={(e) => handleFieldChange('conversationId', e.target.value)}
              placeholder="Conversation UUID"
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          {/* Workspace ID (read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Workspace ID
            </label>
            <input
              type="text"
              value={formData.workspaceId}
              readOnly
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 cursor-not-allowed"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Content *
            </label>
            <textarea
              value={formData.content}
              onChange={(e) => handleFieldChange('content', e.target.value)}
              placeholder="Composition content"
              rows={6}
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          {/* Reasoning */}
          <ReasoningStepsEditor
            value={formData.reasoning}
            onChange={(value) => handleFieldChange('reasoning', value)}
          />

          {/* References */}
          <TagMultiSelector
            value={formData.references}
            onChange={(value) => handleFieldChange('references', value)}
          />

          {/* Last updated */}
          {composer.composition?.updatedAt && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Last updated: {new Date(composer.composition.updatedAt).toLocaleString()}
            </div>
          )}

          {/* Send as Message */}
          <div className="flex flex-col items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Send as:
              </label>
              <select
                value={messageRole}
                onChange={(e) => setMessageRole(e.target.value as 'user' | 'assistant' | 'system')}
                disabled={sending}
                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="user">User</option>
                <option value="assistant">Assistant</option>
                <option value="system">System</option>
              </select>
              <button
                onClick={handleSend}
                disabled={sending || !formData.participantId || !formData.content || !formData.threadId || !formData.conversationId || !formData.workspaceId}
                className="px-6 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {sending ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!searchParticipantId.trim() && !composer.loading && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            Enter a participant ID above to view or edit their composition
          </p>
        </div>
      )}
    </div>
  );
}

